/**
 * Single source of truth for "which app + which tenant does this URL address?"
 *
 * Every routing decision and link-builder in the app derives from the helpers
 * here, so they can never drift apart again. (The bug they replace:
 * `detectAppMode` treated dev.shopai.website as "/engine prefix" mode while
 * `engineSlugPath` dropped the prefix, so a tenant link built as `/dash/v/chat`
 * fell through to the platform-chooser landing page.)
 *
 * Three URL schemes coexist:
 *
 *   1. Per-tenant subdomain  — canonical for dev + prod
 *        <slug>.shopai.website              → tenant <slug>   (prod)
 *        <slug>.dev.shopai.website          → tenant <slug>   (dev)
 *      The slug comes from the HOST; the tenant lives at the origin root, so
 *      in-app paths carry NO slug segment (just "/", "/v/<view>", "/<module>").
 *
 *   2. App subdomain
 *        engine.<env>.shopai.website        → engine admin (+ "/<slug>" tenants)
 *        talkpack.<env>.shopai.website      → talkpack
 *        smartmail.<env>.shopai.website     → smartmail
 *
 *   3. Path prefix  — localhost + the env apex, where you can't use a subdomain
 *        localhost:5174/engine              → engine admin
 *        localhost:5174/engine/<slug>       → tenant <slug>
 *        dev.shopai.website/engine[/<slug>] → same, on the dev box's apex
 *
 * Backend note: the engine API resolves the tenant from the slug-in-path + JWT,
 * never from the Host header (the ALB is a dumb forwarder). So the scheme is a
 * pure frontend + DNS concern — these helpers and the Route53 records are the
 * whole story.
 *
 * All functions take an optional `host` so they can be reasoned about / tested
 * without a browser; they default to the live hostname.
 */

export type AppMode = "engine" | "talkpack" | "smartmail" | "local";

/** First labels that select a top-level app rather than a tenant. */
const APP_SUBDOMAINS: Record<string, Exclude<AppMode, "local">> = {
  engine: "engine",
  talkpack: "talkpack",
  smartmail: "smartmail",
};

/**
 * First labels that are infra / marketing / env-apex — never a tenant slug.
 * Most never resolve to the app's ALB, but once the `*.dev` / `*.apps`
 * wildcards point at the ALB, any stray hostname renders this SPA, so we refuse
 * to treat these as tenants and fall through to the platform chooser instead.
 */
const RESERVED_LABELS = new Set([
  "shopai", "dev", "staging", "app", "www", "api",
  "mail", "mailserver", "smtp", "webmail", "send", "mcp",
  "upload", "livekit", "turn", "vpn", "git", "codereviews",
  "qr", "qr-guide", "cal", "cbc", "askscan", "terms", "privacy",
  "jake", "vox", "devinbox", "prodinbox",
  // "outlook" is the stable hostname baked into the Outlook add-in
  // manifest (outlook.shopai.website -> dev ALB today, prod ALB at
  // cutover). The SPA proper is never the entry point on this host —
  // Outlook only fetches /outlook-addin/* static assets and the
  // /api/engine/outlook/* JSON endpoints. Marking it reserved makes
  // root render the local platform chooser instead of pretending
  // "outlook" is a tenant slug and showing "tenant not found".
  "outlook",
]);

function firstLabel(host: string): string {
  return host.split(".")[0].toLowerCase();
}

/** True for localhost, IPs, and bare/env apex hosts (shopai.website, dev.shopai.website). */
function isApexOrLocalHost(host: string): boolean {
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".localhost")) return true;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return true;
  // "shopai.website" → 2 labels (apex). "dev.shopai.website" → 3 but first is reserved.
  if (host.split(".").length <= 2) return true;
  return RESERVED_LABELS.has(firstLabel(host));
}

/**
 * The tenant slug when the current host is a per-tenant subdomain
 * (dash.dev.shopai.website → "dash"), otherwise null.
 */
export function engineHostSlug(host: string = window.location.hostname): string | null {
  if (isApexOrLocalHost(host)) return null;
  const label = firstLabel(host);
  if (label in APP_SUBDOMAINS) return null; // engine./talkpack./smartmail.
  return label;
}

/** Which top-level app the current host renders. */
export function detectAppMode(host: string = window.location.hostname): AppMode {
  if (isApexOrLocalHost(host)) return "local";
  const label = firstLabel(host);
  if (label in APP_SUBDOMAINS) return APP_SUBDOMAINS[label];
  return "engine"; // any other subdomain is a tenant → rendered by the engine app
}

/**
 * The path prefix the Engine app is mounted under on the current host:
 *   tenant subdomain / engine.* app subdomain → "" (engine is at the origin root)
 *   apex / localhost                          → "/engine"
 */
export function engineBasePath(host: string = window.location.hostname): string {
  return isApexOrLocalHost(host) ? "/engine" : "";
}
